import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { Emdash } from "~/components/Punctuation";
import { formatBpsValue } from "~/utils/formatBpsValue";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { BadgeArrayCell } from "./BadgeArrayCell";
import { AssetTableRowData } from "../types";
import { CatalogReferenceDataSlugs } from "~/modules/catalog/types";

// // // //

export const PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketMinimumSubscriptionAmount,
    sortsOn: AssetColumns.primaryMarketMinimumSubscriptionAmount,
    label: (
        <>
            Min.
            <br />
            Investment
        </>
    ),
    tooltip: CatalogReferenceDataSlugs.primary_market__minimum_subscription_amt,
    type: ColumnTypes.number,
    width: 160,
    loginRequired: true,
    render: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const primaryCurrency =
                asset.primary_market?.base_asset_ticker ?? null;
            const minInitialDeposit =
                asset.primary_market?.subscription_minimum_amount ?? null;

            if (typeof minInitialDeposit !== "number" || !primaryCurrency) {
                return <Emdash />;
            }

            return `${minInitialDeposit.toLocaleString()} ${primaryCurrency}`;
        }

        return <Emdash />;
    },
};

export const PRIMARY_MARKET_BASE_ASSETS_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketBaseAssetTicker,
    sortsOn: AssetColumns.primaryMarketBaseAssetTicker,
    label: "Base Assets",
    tooltip: CatalogReferenceDataSlugs.primary_market__base_asset_ticker,
    type: ColumnTypes.number,
    width: 175,
    loginRequired: true,
    render: (item, { renderOptions }) => {
        if (item.isAsset) {
            // Return emdash if item.primary_markets is empty
            if (!item.primary_markets || item.primary_markets.length === 0) {
                return <Emdash />;
            }

            // Accumulate unique list of allowed investor types
            const baseAssetTickers = uniqueValues(
                item.primary_markets.reduce<string[]>((acc, market) => {
                    const val = market.base_asset_ticker;
                    if (val) {
                        return [...acc, val];
                    }
                    return acc;
                }, []),
            );

            // If there are no allowed investor types, return an emdash
            if (baseAssetTickers.length === 0) {
                return <Emdash />;
            }

            return (
                <BadgeArrayCell
                    values={baseAssetTickers}
                    inline={renderOptions?.inline === true}
                />
            );
        }

        return <Emdash />;
    },
};

export const PRIMARY_MARKET_SUBSCRIPTION_TIME_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketSubscriptionFrequency,
    sortsOn: AssetColumns.primaryMarketSubscriptionFrequency,
    label: "Subscription Time",
    type: ColumnTypes.string,
    width: 175,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const subscription_frequency =
                asset.primary_market?.subscription_frequency ?? null;

            return subscription_frequency;
        }

        return null;
    },
};

export const PRIMARY_MARKET_REDEMPTION_TIME_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketRedemptionFrequency,
    sortsOn: AssetColumns.primaryMarketRedemptionFrequency,
    label: "Redemption Time",
    type: ColumnTypes.string,
    width: 175,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const frequency =
                asset.primary_market?.redemption_frequency || null;

            return frequency;
        }

        return null;
    },
};

export const PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketSubscriptionFeeBps,
    sortsOn: AssetColumns.primaryMarketSubscriptionFeeBps,
    label: (
        <>
            Subscription
            <br />
            Fees
        </>
    ),
    tooltip: CatalogReferenceDataSlugs.primary_market__subscription_fee_bps,
    type: ColumnTypes.percent,
    width: 160,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const value = asset.primary_market?.subscription_fee_bps || 0;

            if (value === null) {
                return null;
            }

            return formatBpsValue(value);
        }

        return null;
    },
};

export const PRIMARY_MARKET_REDEMPTION_FEE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketRedemptionFeeBps,
    sortsOn: AssetColumns.primaryMarketRedemptionFeeBps,
    label: (
        <>
            Redemption
            <br />
            Fees
        </>
    ),
    tooltip: CatalogReferenceDataSlugs.primary_market__redemption_fee_bps,
    type: ColumnTypes.percent,
    width: 155,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const value = asset.primary_market?.redemption_fee_bps || 0;

            if (value === null) {
                return null;
            }

            return formatBpsValue(value);
        }

        return null;
    },
};

export const PRIMARY_MARKET_IS_KYC_REQUIRED_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketIsKYCRequired,
    sortsOn: AssetColumns.primaryMarketIsKYCRequired,
    label: "KYC Required",
    tooltip: CatalogReferenceDataSlugs.primary_market__is_kyc_required,
    type: ColumnTypes.string,
    width: 150,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const isKycRequired = asset.primary_market?.kyc_is_required ?? null;

            if (isKycRequired === null) {
                return null;
            }

            return isKycRequired ? "Required" : "Not Required";
        }

        return null;
    },
};

export const PRIMARY_MARKET_KYC_DESCRIPTION_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "primary_market__kyc_description",
    // sortsOn: AssetColumns.primaryMarketKYCDescription,
    label: "KYC Description",
    type: ColumnTypes.string,
    width: 250,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            return asset.primary_market?.kyc_description ?? null;
        }

        return null;
    },
};

export const PRIMARY_MARKET_SUBSCRIPTION_DESCRIPTION_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketSubscriptionDescription,
    sortsOn: AssetColumns.primaryMarketSubscriptionDescription,
    label: "Subscription Description",
    tooltip: CatalogReferenceDataSlugs.primary_market__subscription_description,
    type: ColumnTypes.string,
    width: 250,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            return asset.primary_market?.subscription_description ?? null;
        }

        return null;
    },
};

export const PRIMARY_MARKET_REDEMPTION_DESCRIPTION_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketRedemptionDescription,
    sortsOn: AssetColumns.primaryMarketRedemptionDescription,
    label: "Redemption Description",
    tooltip: CatalogReferenceDataSlugs.primary_market__redemption_description,
    type: ColumnTypes.string,
    width: 250,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            return asset.primary_market?.redemption_description ?? null;
        }

        return null;
    },
};

export const PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Eligible Investors",
    tooltip: CatalogReferenceDataSlugs.primary_market__investor_types,
    slug: AssetColumns.primaryMarketInvestorTypes,
    sortsOn: AssetColumns.primaryMarketInvestorTypes,
    type: ColumnTypes.string,
    width: 260,
    loginRequired: true,
    render: (item) => {
        if (item.isAsset) {
            // Return emdash if item.primary_markets is empty
            if (!item.primary_markets || item.primary_markets.length === 0) {
                return <Emdash />;
            }

            // Accumulate unique list of allowed investor types
            const allowedInvestorTypes = uniqueValues(
                item.primary_markets.reduce<string[]>((acc, market) => {
                    const val = market.allowed_investor_types || [];
                    return [...acc, ...val];
                }, []),
            );

            // If there are no allowed investor types, return an emdash
            if (allowedInvestorTypes.length === 0) {
                return <Emdash />;
            }

            return <BadgeArrayCell values={allowedInvestorTypes} />;
        }

        return <Emdash />;
    },
};
